/**
 * NoteSheetContext — shared open/close state for the "Ask a quick question"
 * bottom sheet. Consumed by TopBar (Ask button) and Overview (CTA stub),
 * and by NoteSheet itself to self-close after a successful send.
 */
import { createContext, useContext, useState, useCallback, useRef } from 'react';

const NoteSheetContext = createContext(null);

export function NoteSheetProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  // Captures the element that triggered open() so focus returns on close.
  const triggerRef = useRef(null);

  const open = useCallback(() => {
    // Store the currently focused element before the sheet takes focus.
    triggerRef.current = document.activeElement;
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Restore focus to the opener after the sheet unmounts (next frame).
    const trigger = triggerRef.current;
    triggerRef.current = null;
    if (trigger && typeof trigger.focus === 'function') {
      requestAnimationFrame(() => trigger.focus());
    }
  }, []);

  return (
    <NoteSheetContext.Provider value={{ isOpen, open, close }}>
      {children}
    </NoteSheetContext.Provider>
  );
}

export function useNoteSheet() {
  const ctx = useContext(NoteSheetContext);
  if (!ctx) throw new Error('useNoteSheet must be used inside NoteSheetProvider');
  return ctx;
}
